import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaAndroid, FaApple, FaWindows } from "react-icons/fa";
import { FiCalendar, FiExternalLink, FiInfo, FiX } from "react-icons/fi";

const APPLE_SUPPORT_URL =
  "https://support.apple.com/nl-nl/guide/calendar/icl1022/mac";
const MICROSOFT_SUPPORT_URL =
  "https://support.microsoft.com/outlook/import-or-subscribe-to-a-calendar-in-outlook-com-or-outlook-on-the-web-cff1429c-5af6-41ec-a5b4-74f2c278e98c";
const GOOGLE_SUPPORT_URL = "https://support.google.com/calendar/answer/37100";

const getDetectedPlatformKey = () => {
  const ua = navigator.userAgent || "";

  if (/iPhone|iPad|Macintosh|Mac OS X/i.test(ua)) return "apple";
  if (/Android/i.test(ua)) return "android";
  if (/Windows/i.test(ua)) return "windows";

  return null;
};

const platformCards = [
  {
    key: "apple",
    title: "Apple Calendar",
    subtitle: "iPhone, iPad and Mac",
    intro: "Tap or click the feed icon to add the calendar.",
    url: APPLE_SUPPORT_URL,
    linkText: "Apple support",
    Icon: FaApple,
    iconClassName: "text-black dark:text-white",
  },
  {
    key: "windows",
    title: "Windows / Outlook",
    subtitle: "Outlook and Microsoft 365",
    intro: "Click the feed icon and confirm the subscription.",
    url: MICROSOFT_SUPPORT_URL,
    linkText: "Microsoft support",
    Icon: FaWindows,
    iconClassName: "text-sky-600 dark:text-sky-400",
  },
  {
    key: "android",
    title: "Android / Google Calendar",
    subtitle: "Usually by copying the link",
    intro: "Use the copy icon and add the calendar by URL.",
    url: GOOGLE_SUPPORT_URL,
    linkText: "Google support",
    Icon: FaAndroid,
    iconClassName: "text-lime-600 dark:text-lime-400",
  },
];

const CalendarSubscriptionHelpModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  if (!open) return null;

  const detectedPlatformKey = getDetectedPlatformKey();

  const sortedPlatformCards = [...platformCards].sort((a, b) => {
    if (a.key === detectedPlatformKey) return -1;
    if (b.key === detectedPlatformKey) return 1;
    return 0;
  });

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex min-h-screen items-center justify-center overflow-y-auto bg-black/40 p-3 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="calendar-subscription-help-title"
    >
      <div className="relative my-4 w-full max-w-xl rounded-3xl border border-white/40 bg-white/95 p-5 shadow-2xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.actions.close", { defaultValue: "Close" })}
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
        >
          <FiX className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 shadow-inner dark:bg-emerald-950/50 dark:text-emerald-200">
            <FiCalendar className="h-7 w-7" aria-hidden="true" />
          </div>

          <h2
            id="calendar-subscription-help-title"
            className="text-2xl font-bold text-slate-950 dark:text-white"
          >
            {t("parentPortal.calendar.add_agenda", { defaultValue: "Add calendar" })}
          </h2>

          <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            {t("parentPortal.calendar.subscription_auto_updates_detail", {
              defaultValue: "A calendar subscription updates automatically. New appointments and changes will appear in your calendar automatically.",
            })}
          </p>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {sortedPlatformCards.map(
            ({ key, title, subtitle, intro, url, linkText, Icon, iconClassName }) => {
              const isDetected = key === detectedPlatformKey;

              return (
                <section
                  key={key}
                  className={`relative flex gap-3 rounded-2xl border p-4 shadow-sm ${
                    isDetected
                      ? "border-emerald-300 bg-emerald-50/80 ring-1 ring-emerald-200 dark:border-emerald-700 dark:bg-emerald-950/40 dark:ring-emerald-900"
                      : "border-slate-200 bg-white/80 dark:border-slate-700 dark:bg-slate-950/40"
                  }`}
                >
                  {isDetected && (
                    <span className="absolute right-3 top-3 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                      {t("parentPortal.calendar.recommended", { defaultValue: "Recommended" })}
                    </span>
                  )}

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-50 dark:bg-slate-900">
                    <Icon
                      className={`h-7 w-7 ${iconClassName}`}
                      aria-hidden="true"
                    />
                  </div>

                  <div className="min-w-0 flex-1 pr-1">
                    <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                      {t(`parentPortal.calendar.help_platforms.${key}.title`, { defaultValue: title })}
                    </h3>

                    <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                      {t(`parentPortal.calendar.help_platforms.${key}.subtitle`, { defaultValue: subtitle })}
                    </p>

                    <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                      {t(`parentPortal.calendar.help_platforms.${key}.intro`, { defaultValue: intro })}
                    </p>

                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 transition hover:text-emerald-900 hover:underline dark:text-emerald-300 dark:hover:text-emerald-100"
                    >
                      {t(`parentPortal.calendar.help_platforms.${key}.link`, { defaultValue: linkText })}
                      <FiExternalLink aria-hidden="true" />
                    </a>
                  </div>
                </section>
              );
            }
          )}

          <section className="flex gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4 shadow-sm dark:border-emerald-900/50 dark:bg-emerald-950/30">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-white text-emerald-700 dark:border-emerald-800 dark:bg-slate-950 dark:text-emerald-300">
              <FiInfo className="h-5 w-5" aria-hidden="true" />
            </div>

            <div>
              <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                {t("parentPortal.calendar.other_calendar_app", { defaultValue: "Another calendar app?" })}
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                {t("parentPortal.calendar.other_calendar_app_hint", {
                  defaultValue: "The feed link works in most calendar apps. Use the feed icon or copy button to add the link manually.",
                })}
              </p>
            </div>
          </section>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-full bg-gradient-to-r from-emerald-600 to-teal-700 py-3 text-base font-semibold text-white shadow-lg transition hover:opacity-95"
        >
          {t("common.actions.close", { defaultValue: "Close" })}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default CalendarSubscriptionHelpModal;
